<template>
  <q-card
    flat
  >
    <q-card-section
      class="row justify-center q-px-none q-py-none"
      :class="{ 'session-details-card__header--sticky': navController }"
    >
      <div class="col row items-center">
        <q-input
          v-if="editSessionName"
          ref="sessionName"
          v-model.trim="editSession.sessionName"
          :label="$t('sessions.sessionCard.inputSessionNameLabel')"
          :rules="[val => !!val || $t('sessions.sessionCard.inputProgramNameValidator')]"
          lazy-rules
          autofocus
          dense
          class="col"
          @blur="onSaveSessionName"
          @keyup.enter="$refs.sessionName.blur()"
        />
        <h5
          v-else
          class="q-my-none"
          :data-e2e="`header-${sessionNameFormatted}`"
          @click="onEditSessionName"
        >
          {{ sessionNameFormatted }}
          <q-btn
            v-if="userCanEditData && !strictSessionNaming"
            class="q-ml-sm"
            padding="4px 8px"
            color="grey-5"
            flat
            icon="drive_file_rename_outline"
          />
        </h5>
      </div>
      <q-space />
      <div class="row col-auto items-center q-col-gutter-sm no-wrap">
        <div
          v-if="showNav"
          class="row items-center no-wrap"
        >
          <hr-icon-action-btn
            icon="chevron_left"
            aria-label="previous-activity"
            :disable="!navController.hasPrev || isNavBusy"
            @click="onNavPrev"
          >
            <hr-tooltip>{{ $t('common.buttonDirectionPrevious') }}</hr-tooltip>
          </hr-icon-action-btn>
          <hr-icon-action-btn
            icon="chevron_right"
            aria-label="next-activity"
            :loading="navController.isLoading"
            :disable="!navController.hasNext || isNavBusy"
            @click="onNavNext"
          >
            <hr-tooltip>{{ $t('common.buttonDirectionNext') }}</hr-tooltip>
          </hr-icon-action-btn>
        </div>
        <div>
          <q-btn
            v-if="hasTrainerCredentials"
            flat
            round
            dense
            color="dark-variant"
            icon="more_vert"
          >
            <q-menu
              v-model="showMenu"
              @hide="onCloseVisibilityMenu"
            >
              <q-list
                style="min-width: 100px"
              >
                <q-item
                  v-if="canAddSessionActivity"
                  v-close-popup
                  clickable
                  @click="onAddSessionActivity"
                >
                  <q-item-section
                    avatar
                    class="text-no-wrap"
                  >
                    <q-icon
                      name="more_time"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section class="text-no-wrap">
                    {{ $t('sessions.sessionCard.textAddSessionActivity') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-ripple
                  v-close-popup
                  clickable
                  @click="onCopyDataToClipboard"
                >
                  <q-item-section
                    avatar
                  >
                    <q-icon
                      name="assessment"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section
                    class="text-no-wrap"
                  >
                    {{ $t('sessions.sessionCard.textExportDataToClipboard') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="userCanEditSessionData"
                  v-close-popup
                  clickable
                  @click="onEditSessionProgramsData"
                >
                  <q-item-section
                    avatar
                    class="text-no-wrap"
                  >
                    <q-icon
                      name="edit"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section class="text-no-wrap">
                    {{ $t('sessions.sessionCard.textEditSessionData') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="editable && hasSupervisorCredentials && isDone"
                  :disable="offlineMode"
                  clickable
                >
                  <q-item-section avatar>
                    <q-icon
                      name="visibility"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section>
                    {{ $t('sessions.sessionCard.textVisibility') }}
                  </q-item-section>
                  <q-item-section side>
                    <q-icon
                      name="keyboard_arrow_right"
                      color="dark"
                    />
                  </q-item-section>
                  <q-menu
                    v-if="!offlineMode"
                    @hide="onCloseVisibilityMenu"
                  >
                    <q-item>
                      <q-item-section>
                        <q-toggle
                          :model-value="visibilitySettings.limitedVisibility"
                          :label="$t('sessions.sessionCard.toggleLimitVisibilityLabel')"
                          checked-icon="visibility"
                          :val="visibilitySettings.value"
                          color="green"
                          unchecked-icon="visibility_off"
                          @update:model-value="onUpdateVisibilitySetting({ limitedVisibility: $event })"
                        />
                      </q-item-section>
                    </q-item>
                    <template
                      v-if="visibilitySettings.limitedVisibility"
                    >
                      <q-separator />
                      <q-item
                        v-for="visibilityOption in visibilityOptions"
                        :key="visibilityOption.value"
                        tag="label"
                      >
                        <q-item-section
                          class="col-auto"
                        >
                          <q-toggle
                            :model-value="visibilitySettings.visibility"
                            :val="visibilityOption.value"
                            :disable="visibilityOption.disable"
                            checked-icon="visibility"
                            color="green"
                            unchecked-icon="visibility_off"
                            @update:model-value="onToggleVisibility(visibilityOption.value)"
                          />
                        </q-item-section>
                        <q-item-section class="col">
                          <q-item-label>
                            {{ visibilityOption.label }}
                          </q-item-label>
                          <q-item-label caption>
                            {{ visibilityOption.hint }}
                          </q-item-label>
                        </q-item-section>
                      </q-item>
                    </template>
                  </q-menu>
                </q-item>
                <q-item
                  v-if="editable && userCanEditSessionTime && isDone"
                  v-close-popup
                  clickable
                  @click="onEditSessionTime"
                >
                  <q-item-section
                    avatar
                    class="text-no-wrap"
                  >
                    <q-icon
                      name="edit_calendar"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section class="text-no-wrap">
                    {{ $t('sessions.sessionCard.textEditSessionTime') }}
                  </q-item-section>
                </q-item>
                <q-item
                  v-if="userCanDeleteSession"
                  v-close-popup
                  clickable
                  :disable="hasLinkedAppointments || cantDeleteActivities"
                  @click="onDeleteSession"
                >
                  <q-item-section
                    avatar
                    class="text-no-wrap"
                  >
                    <q-icon
                      name="delete"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section class="text-no-wrap">
                    <universal-tooltip
                      :show-tooltip="hasLinkedAppointments || cantDeleteActivities"
                      :text="$t(hasLinkedAppointments
                        ? 'sessions.sessionCard.shouldUnlinkAllAppointments'
                        : 'sessions.sessionCard.missingSessionActivitiesDeletePrivilege'
                      )"
                    >
                      {{ $t('sessions.sessionCard.textDeleteSession') }}
                    </universal-tooltip>
                  </q-item-section>
                </q-item>
                <q-item
                  v-close-popup
                  clickable
                  @click="onCopySupportInfo"
                >
                  <q-item-section
                    avatar
                    class="text-no-wrap"
                  >
                    <q-icon
                      name="content_copy"
                      color="dark-variant"
                    />
                  </q-item-section>
                  <q-item-section class="text-no-wrap">
                    {{ $t('common.copySupportInfo') }}
                  </q-item-section>
                </q-item>
              </q-list>
            </q-menu>
          </q-btn>
        </div>
        <div v-if="closeButton">
          <q-btn
            flat
            round
            dense
            icon="close"
            color="dark"
            data-e2e="button-close-session-card"
            @click="onCloseDialog"
          />
        </div>
      </div>
    </q-card-section>
    <q-card-section
      key="sessionData"
      class="q-px-none"
    >
      <banner
        v-if="showMissingSessionProgramsBanner"
        type="warning"
        :message="$t('sessions.sessionCard.reloadSessionProgramsMessage')"
        class="q-mb-md"
      >
        <template #action>
          <q-btn
            flat
            :label="$t('sessions.sessionCard.btnReloadSessionPrograms')"
            @click="loadSessionData"
          />
        </template>
      </banner>
      <template
        v-else-if="!editData && !$q.loading.isActive && hasOtherLocalBackup && !hideAllBanners && !offlineMode"
      >
        <banner
          v-if="showBackupData"
          type="error"
          variant="multi-line"
          :message="$t('sessions.sessionCard.backupCopyMessage')"
          class="q-mb-md"
        >
          <template #action>
            <q-btn
              flat
              :label="$t('sessions.sessionCard.btnViewRemoteDataLabel')"
              @click="onViewOriginalData"
            />
            <q-btn
              flat
              :label="$t('sessions.sessionCard.btnSaveBackupDataLabel')"
              @click="onRestoreBackupData"
            />
          </template>
        </banner>
        <banner
          v-else
          type="error"
          :message="$t('sessions.sessionCard.localCopyMessage')"
          class="q-mb-md"
        >
          <template #action>
            <q-btn
              flat
              :label="$t('sessions.sessionCard.btnViewDataLabel')"
              @click="onViewLocalBackup"
            />
          </template>
        </banner>
      </template>
      <banner
        v-if="showSendBanner"
        :data-e2e="`banner-appointment-${appointmentBannerType}`"
        :type="appointmentBannerType"
        :message="appointmentBannerText"
        show-icon
        subtype="appointment"
        class="q-mb-md"
      >
        <template
          v-if="
            !isLoadingAppointments
              && !areAllActivitiesRendered
          "
          #action
        >
          <div class="row items-center q-col-gutter-sm">
            <div
              v-if="isSomeNotesReadyToBeSend"
            >
              <q-btn
                data-e2e="button-send-now"
                :label="$t('sessions.sessionCard.buttonSendNow')"
                :loading="isSendingSessionNote"
                :disable="offlineMode"
                flat
                @click="onTransferSessionNote()"
              >
                <q-tooltip v-if="offlineMode">
                  {{ $t('common.notAvailableOffline') }}
                </q-tooltip>
              </q-btn>
            </div>
          </div>
        </template>
      </banner>
      <template
        v-if="!readOnly"
      >
        <session-activity-item
          v-for="sessionActivity in sortedAndFilteredSessionActivities"
          :key="sessionActivity.id"
          :ref="(el) => setSessionActivityItemRef(sessionActivity.id, el)"
          :session-activity="sessionActivity"
          :expand="sessionActivityExpand[sessionActivity.id]"
          :bold="isAnyExpanded && sessionActivityExpand[sessionActivity.id]"
          :unfocused="isAnyExpanded && !sessionActivityExpand[sessionActivity.id]"
          :apply-immediately="applyImmediately && (openSessionActivity === sessionActivity.id)"
          :linked-appointment="getLinkedAppointment(sessionActivity)"
          :session="editSession"
          :all-programs="allPrograms"
          :client="client"
          :notes-sending="isSendingSessionNote"
          :has-other-notes-backup="activityHasNotesBackup(sessionActivity.id)"
          data-e2e="expand-notes-for-session-activity"
          @input="onSwitchSessionActivity(sessionActivity, $event)"
          @changed="onSetSessionActivityChanged(sessionActivity, $event)"
          @show-backup-notes="onShowBackupNotes(sessionActivity, $event)"
          @delete-request="onSessionActivityDeleteRequest"
          @appointments-updated="linkedAppointmentsUpdated()"
          @session-updated="onSessionUpdated"
          @timestamps-changed="onSessionActivityTimestampsChanged(sessionActivity)"
          @scroll-to-banner="scrollToBannerEvent"
          @send-to-integration="onTransferSessionNote"
        />
      </template>
      <div
        ref="sessionDataAnchor"
        class="q-mx-md q-pt-md"
      >
        <q-slider
          v-if="hasTimecodedData"
          v-model="currentTimestamp"
          :max="sessionDuration"
          :inner-min="minTimestamp"
          :inner-max="maxTimestamp"
          :step="0"
          label-always
          label
          :label-value="currentTimestampFormatted"
          inner-track-color="primary-light"
          class="full-width q-mt-md"
          @change="onGotoTimestamp"
        />
      </div>
      <div
        class="q-gutter-md q-mb-md"
      >
        <div
          v-for="sessionProgram in sessionProgramsWithData"
          :key="sessionProgram.id"
        >
          <div v-if="isSessionProgramVisible(sessionProgram)">
            <trial-overview
              ref="trialOverviews"
              :session-program="sessionProgram"
              :program="getProgram(sessionProgram)"
              :session="editSession"
              :data-notes="getDataNotes(sessionProgram)"
              :edit="editData"
              :from-time="minTimestamp"
              :time="currentTimestamp"
              :show-discriminative-stimulus="showDiscriminativeStimulus"
              is-workflows-preloaded
              :is-preload-complete="isPreloadComplete"
              :session-program-ids="sessionProgramIds"
              :session-behavior-occurrences="sessionBehaviorOccurrences"
              @data-changed="onProgramsDataChanged(sessionProgram)"
              @data-notes-changed="onProgramsDataNotesChanged(sessionProgram, $event)"
              @program-notes-changed="onSessionProgramDataNotesChanged(sessionProgram, $event)"
              @seek="onSeek"
            />
            <q-scroll-area
              v-if="hasSessionProgramFiles(sessionProgram)"
              class="q-mt-md full-width row assets-container"
            >
              <div class="row no-wrap q-mr-sm assets-container items-center">
                <div
                  v-for="fileInfo in getSessionProgramFiles(sessionProgram)"
                  :key="'chip_' + fileInfo.id"
                  class="asset"
                >
                  <asset
                    :asset="fileInfo"
                    :session="editSession"
                    :can-change-file="hasSupervisorCredentials"
                    :loading="fixVideosLoading"
                    :current-time="currentTimestamp"
                    @play-video="onPlayVideo"
                    @update-video-info="onUpdateVideoInfo"
                    @delete-file="onFileDelete"
                  />
                </div>
              </div>
            </q-scroll-area>
            <div
              v-if="hasTaskList(sessionProgram)"
              class="q-pt-md"
            >
              <task-list-card
                :ref="'taskList_'+sessionProgram.id"
                flat
                :client="client"
                :task-list="getTaskList(sessionProgram)"
                :filter="getTaskListItems(sessionProgram)"
                open
                :session="session.id"
              />
            </div>
          </div>
        </div>
      </div>
      <q-card-actions
        v-if="editData"
        class="row justify-end q-mb-lg"
      >
        <q-space />
        <q-btn
          flat
          :label="$t('common.buttonCancel')"
          color="secondary"
          @click="onCancelEditSessionPrograms"
        />
        <q-btn
          flat
          :label="$t('sessions.sessionCard.buttonSaveSession')"
          color="primary"
          @click="onSaveSessionProgramsData"
        />
      </q-card-actions>
      <q-scroll-area
        v-if="hasRootSessionFiles"
        class="q-mt-md full-width row assets-container asset-margin-bottom"
      >
        <div class="row no-wrap q-mr-sm assets-container items-center">
          <div
            v-for="fileInfo in rootSessionFiles"
            :key="'chip_' + fileInfo.id"
            class="asset"
          >
            <asset
              :asset="fileInfo"
              :all-files="sessionFiles"
              :session="editSession"
              :can-change-file="hasSupervisorCredentials"
              :loading="fixVideosLoading"
              :current-time="currentTimestamp"
              :editable="editable"
              @play-video="onPlayVideo"
              @update-video-info="onUpdateVideoInfo"
              @delete-file="onFileDelete"
            />
          </div>
        </div>
      </q-scroll-area>
      <div
        v-if="(userCanEditData || isParent) && !isSessionTypeWithoutPrograms"
      >
        <file-attachment
          :session="editSession"
          :disable="offlineMode"
          :file-path="'/sessions/' + editSession.id + '/'"
          :label="$t('run.done.textAttachVideos')"
          color="primary"
          flat
          size="md"
        >
          <q-tooltip>
            {{ $t('sessions.sessionCard.buttonAttachFile') }}
          </q-tooltip>
        </file-attachment>
      </div>
      <message-thread
        :messages="sessionMessages"
        :thread="`session_${editSession.id}`"
        origin="session"
        :origin-id="editSession.id"
        :client="session.client"
        :current-timestamp="messageTimestamp"
        :loading="isLoadingComments"
        :read-only="readOnly"
        :prevent-comment="!userCanCommentSession"
        :mood-reaction="session.moodReaction"
        :add-comment-hint="$t('sessions.sessionCard.hintAddComment')"
        :visibility-settings="visibilitySettings"
        @click="onClickChatMessage"
        @message-read="onMessageRead"
      />
    </q-card-section>
  </q-card>
</template>
<script>
import { Clipboard } from '@capacitor/clipboard';
import { callFirebaseFunction } from 'boot/firebase';
import FileAttachment from 'components/FileAttachment';
import MessageThread from 'components/Messages/MessageThread';
import EditSessionActivityDialog from 'components/SessionActivities/Modals/EditSessionActivityDialog';
import SessionActivityItem from 'components/SessionActivities/SessionActivityItem';
import Asset from 'components/SessionAssets/Asset';
import EditSessionDetailsDialog from 'components/Sessions/Modals/EditSessionDetailsDialog';
import TrialOverview from 'components/Sessions/TrialOverview';
import Banner from 'components/UI/Banner';
import UniversalTooltip from 'components/UI/UniversalTooltip';
import { HrIconActionBtn, HrTooltip } from 'src/design-system';
import { Timestamp } from 'firebase/firestore';
import {
  assign, chain, compact, filter, find, findIndex, forEach, get, has, includes,
  isEmpty, isEqual, isNull, isString, map, mapValues, omit, reject, size, unionBy,
} from 'lodash';
import { mapActions, mapState } from 'pinia';
import { copyToClipboard, scroll } from 'quasar';
import { useTime } from 'src/composables/time';
import AppointmentsDB from 'src/fb/appointments-db';
import DataDB from 'src/fb/data-db';
import DataNotesDB from 'src/fb/dataNotes-db';
import MessagesDB from 'src/fb/messages-db';
import SessionActivitiesDB from 'src/fb/sessionActivities-db';
import SessionFilesDB from 'src/fb/sessionFiles-db';
import SessionProgramsDB from 'src/fb/sessionPrograms-db';
import SessionsDB from 'src/fb/sessions-db';
import TaskListsDB from 'src/fb/taskLists-db';
import Activities from 'src/mixins/Activities';
import Auth from 'src/mixins/Auth';
import Integrations from 'src/mixins/Integrations';
import Interactions from 'src/mixins/Interactions';
import SessionActivitiesMixin from 'src/mixins/SessionActivitiesMixin';
import SessionCardsCommon from 'src/mixins/SessionCardsCommon';
import SessionExportMixin from 'src/mixins/SessionExportMixin';
import Targets from 'src/mixins/Targets';
import Toolbox from 'src/mixins/Toolbox';
import { auth } from 'src/store/auth';
import { firebaseUploader } from 'src/store/firebaseUploader';
import { network } from 'src/store/network';
import { programsTargets } from 'src/store/programsTargets';
import { promptLevels } from 'src/store/promptLevels';
import { sessions } from 'src/store/sessions';
import { workflows } from 'src/store/workflows';
import { copySupportInfoToClipboard, notifyErrorWithDetails } from 'src/utils/AppUtils';
import { clearClientBackupData } from 'src/utils/backupUtils';
import { isBehaviorOccurrence } from 'src/utils/behaviorOccurrences';
import { logEvent } from 'src/utils/LogEvent';
import { defineAsyncComponent } from 'vue';

export default {
  name: 'SessionDetailsCard',

  components: {
    Asset,
    FileAttachment,
    MessageThread,
    SessionActivityItem,
    TaskListCard: defineAsyncComponent(() => import('components/Clients/TaskListCard')),
    TrialOverview,
    UniversalTooltip,
    Banner,
    HrIconActionBtn,
    HrTooltip,
  },

  mixins: [
    Activities,
    Auth,
    Integrations,
    Interactions,
    SessionActivitiesMixin,
    SessionCardsCommon,
    SessionExportMixin,
    Toolbox,
    Targets,
  ],

  async beforeRouteLeave(to, from, next) {
    await this.unbindSessionActivities();
    await this.unbindBackupsData();
    await this.unbindBackupNotes();
    next();
  },

  props: {
    // All programs for current client
    allPrograms: {
      type: Array,
      required: true,
    },
    client: {
      type: Object,
      required: true,
    },
    editable: {
      type: Boolean,
      default: false,
    },
    currentTime: {
      type: Number,
      default: null,
    },
    isEditingData: {
      type: Boolean,
      default: false,
    },
    openSessionActivity: {
      type: String,
      default: null,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
    closeButton: {
      type: Boolean,
      default: false,
    },
    applyImmediately: {
      type: Boolean,
      default: false,
    },
    // Controller from useActivityNavigation; when set, the header shows back/next.
    navController: {
      type: Object,
      default: null,
    },
  },

  emits: [
    // in ViewSession component redirect to History page
    'changed',
    'close',
    'play-video',
    'video-seek',
    'update-data',
    'update-video-info',
    'edit-data',
    'edit-session-notes',
    'delete-session',
    'delete-session-activity',
    'add-session-activity',
    'scroll-to-banner',
  ],

  setup() {
    const { getLocaleDateString, getLocaleTimeString, extractDate, formatDate } = useTime();
    return { getLocaleDateString, getLocaleTimeString, extractDate, formatDate };
  },

  data() {
    return {
      showMenu: false,
      editData: false,
      showBackupData: false,
      hideAllBanners: false,
      showNotesBackup: false,

      isLoadingSessionActivities: false,
      isLoadingAppointments: false,
      isLoadingComments: false,
      isSavingComment: false,
      isSendingSessionNote: false,
      isDataChanged: false,
      sessionActivityChanged: {},

      sessionClient: {},

      linkedAppointments: [],
      editSession: null,
      sessionPrograms: [],
      sessionFiles: [],
      dataNotes: [],
      sessionMessages: [],
      sessionTaskLists: [],

      /* Holds function to unwatch VideoPlayer from store */
      unwatchVideo: null,
      currentTimestamp: 0,
      minTimestamp: undefined,
      maxTimestamp: undefined,
      focusedSessionActivity: null,
      sessionActivityExpand: {},

      fixVideosLoading: false,

      editSessionName: false,

      programsDataChanged: false,

      showDiscriminativeStimulus: false,

      sessionActivitiesWithBackupNotes: [],
      sessionActivities: [],
      isPreloadComplete: false,
    };
  },

  computed: {
    ...mapState(auth, ['userDetails', 'connectedClinic', 'allowImmediateSync', 'strictSessionNaming',
      'hasSupervisorCredentials', 'hasTherapistCredentials',
      'hasTrainerCredentials', 'isParent', 'isTrainer', 'isSubscriptionPremium', 'integrationSettings',
    ]),
    ...mapState(firebaseUploader, ['uploadQueue']),
    ...mapState(network, ['offlineMode']),

    hasSessionPrograms() {
      return !isEmpty(this.sessionPrograms);
    },

    userCanEditData() {
      if (this.readOnly) {
        return false;
      }
      if (this.hasTherapistCredentials) {
        return true;
      }
      // RBTs can only edit data if they are adding a manual session
      return ((this.isTrainer)
        && (this.session.runBy === this.userDetails.userId));
    }, // userCanEditData()

    userCanEditSessionData() {
      const hasData = this.sessionPrograms.some((value) => value.data?.data?.length);
      const initialManualDataEntry = !hasData && this.isOwnSession;

      return this.editable
        && this.hasEditableData
        && this.isDone
        && (this.userHasPrivilege('sessions/editData') || initialManualDataEntry);
    },

    userCanDeleteSession() {
      return this.editable
        && this.isDone
        && this.hasTrainerCredentials
        && this.userHasPrivilege('sessions/delete');
    },

    isSessionTypeWithoutPrograms() {
      return ['form'].includes(this.editSession.sessionType);
    },

    showMissingSessionProgramsBanner() {
      return !this.isSessionTypeWithoutPrograms
        && !this.hasSessionPrograms
        && !this.$q.loading.isActive
        && !this.hideAllBanners;
    },

    isAnyExpanded() {
      return chain(this.sessionActivityExpand)
        .values()
        .some()
        .value();
    },

    showSendBanner() {
      return (
        this.connectedClinic.integration
        && !this.hideAllBanners
        && !this.isParent
        && (
          !isEmpty(this.linkedAppointments)
          || this.sessionActivities.some(
            (sessionActivity) => this.canTransferStaffActivityWithoutLinkedAppointment(sessionActivity, true),
          )
        )
      );
    },

    isSomeNotesReadyToBeSend() {
      return this.sessionActivities.some((sessionActivity) => {
        return (
          !!sessionActivity.appointment
          && (
            sessionActivity.status === 'closed'
            || (sessionActivity.status === 'pending-signatures' && this.allowImmediateSync
              // For beta Camber + Scheduling we don't allow immediate sync
              && !this.integrationSettings.allowScheduling)
          )
        ) || this.canTransferStaffActivityWithoutLinkedAppointment(sessionActivity);
      });
    },

    areActivitiesContainPendingReview() {
      return this.sessionActivities.filter((sessionActivity) => sessionActivity.appointment)
        .some((sessionActivity) => sessionActivity.status === 'pending-review');
    },

    areAllActivitiesRendered() {
      const transferableActivities = this.sessionActivities.filter((sessionActivity) => (
        !!sessionActivity.appointment || this.canTransferStaffActivityWithoutLinkedAppointment(sessionActivity)
      ));

      if (!transferableActivities.length) {
        return false;
      }

      return transferableActivities.every((sessionActivity) => (
        sessionActivity.status === 'rendered'
        || (
          !!sessionActivity.appointment
          && get(this.getLinkedAppointment(sessionActivity), 'isRendered')
          && this.allowImmediateSync
        )
      ));
    },

    areAllActivitiesIntegrationCompleted() {
      return this.sessionActivities.every((sessionActivity) => sessionActivity.status === 'integration-completed');
    },

    userCanCommentSession() {
      return this.userHasPrivilege('sessions/addComment');
    }, // userCanCommentSession()

    isDone() {
      return ['done', 'processing', 'offline-done'].includes(this.session.status);
    },

    hasRootSessionFiles() {
      return !isEmpty(this.rootSessionFiles);
    }, // hasRootSessionFiles()

    hasEditableData() {
      return (!this.editData)
        && !isEmpty(this.sessionPrograms)
        && this.userCanEditData;
    }, // hasEditableData()

    hasLinkedAppointments() {
      return !!(find(this.sessionActivities, (sessionActivity) => !isNull(sessionActivity.appointment)));
    }, // hasLinkedAppointments()

    cantDeleteActivities() {
      return this.sessionActivities?.length && !this.userHasPrivilege('sessionActivities/delete');
    }, // cantDeleteActivities()

    isAnySessionActivityChanged() {
      return chain(this.sessionActivityChanged)
        .values()
        .some()
        .value();
    },

    // A note transfer must finish against the card that started it, otherwise its
    // completion handlers run after navigation has unmounted this one.
    isNavBusy() {
      return !!this.navController?.isLoading || this.isSendingSessionNote;
    }, // isNavBusy()

    showNav() {
      return !!this.navController && (this.navController.hasPrev || this.navController.hasNext);
    }, // showNav()

    sortedAndFilteredSessionActivities() {
      const processedSessionActivities = this.showNotesBackup
        ? this.sessionActivitiesWithBackupNotes
        : this.sessionActivities;

      const getTimestampMillis = (timestamp) => (
        timestamp?.toMillis?.() ?? 0
      );

      return chain(processedSessionActivities)
        .reject({ isDeleted: true })
        .sort((first, second) => {
          const startDifference = getTimestampMillis(first.startTimestamp)
            - getTimestampMillis(second.startTimestamp);

          if (startDifference !== 0) {
            return startDifference;
          }

          const endDifference = getTimestampMillis(first.endTimestamp)
            - getTimestampMillis(second.endTimestamp);

          if (endDifference !== 0) {
            return endDifference;
          }

          const createDifference = getTimestampMillis(first.createTimestamp)
            - getTimestampMillis(second.createTimestamp);

          if (createDifference !== 0) {
            return createDifference;
          }

          return String(first.id).localeCompare(String(second.id));
        })
        .value();
    },

    rootSessionFiles() {
      return reject(this.sessionFiles, 'sessionProgram');
    }, // rootSessionFiles()

    sessionProgramFiles() {
      return filter(this.sessionFiles, 'sessionProgram');
    }, // rootSessionFiles()

    sessionTypeHasSessionNotes() {
      return get(this.session, 'sessionType') !== 'training';
    }, // sessionTypeHasSessionNotes()

    sessionNotesLocked() {
      return get(this.session, 'sessionNotesLocked');
    }, // sessionNotesLocked()

    sessionUrl() {
      const host = includes(['development', 'staging'], process.env.ENV_TYPE)
        ? 'hi-rasmus-staging.firebaseapp.com'
        : 'app.hirasmus.com';
      const client = get(this.session, 'client', '');

      return `https://${host}/#/history/${this.session.id}?client=${client}`;
    }, // sessionUrl()

    canAddSessionActivity() {
      return this.hasTherapistCredentials;
    },

    appointmentBannerText() {
      if (this.isLoadingAppointments) {
        return this.$t('common.loading');
      }

      const integration = this.getIntegrationName(this.connectedClinic.integration);

      if (this.isSendingSessionNote) {
        return this.$t('sessions.sessionCard.textTransferring', {
          integration,
        });
      }

      if (this.areAllActivitiesRendered) {
        return this.$t('sessions.sessionCard.textSessionRendered', {
          integration,
        });
      }

      if (this.isSomeNotesReadyToBeSend) {
        return this.$t('sessions.sessionCard.textTransferReady', {
          integration,
        });
      }

      if (this.areActivitiesContainPendingReview) {
        return this.$t('sessions.sessionCard.textTransferPendingReview', {
          integration,
        });
      }

      if (this.areAllActivitiesIntegrationCompleted) {
        return this.$t('sessions.sessionCard.textSessionIntegrationCompleted', {
          integration,
        });
      }

      return this.$t('sessions.sessionCard.textTransferPendingNotes', {
        integration,
      });
    }, // appointmentBannerText()

    sessionProgramsWithData() {
      if (!this.showBackupData) {
        return this.sessionPrograms;
      }
      return map(
        this.sessionPrograms,
        (sessionProgram) => ({
          ...sessionProgram,
          data: chain(this.backupData)
            .get(sessionProgram.id)
            .omit(['updateTimestamp', 'createTimestamp'])
            .value(),
        }),
      );
    }, // sessionProgramsWithData()

    sessionProgramIds() {
      return this.sessionPrograms.map(({ program }) => program?.id || program);
    }, // sessionProgramIds()

    /** Every behavior occurrence in the session, with the Behavior Tracking program that holds it. */
    sessionBehaviorOccurrences() {
      return this.sessionProgramsWithData.flatMap((sessionProgram) => {
        const behaviorProgram = this.getProgram(sessionProgram);

        return (sessionProgram.data?.data ?? [])
          .filter(isBehaviorOccurrence)
          .map((occurrence) => ({
            ...occurrence,
            behaviorProgram: behaviorProgram?.id,
            behaviorProgramName: behaviorProgram?.programName,
            behaviorProgramPromptLevelTemplate: behaviorProgram?.promptLevelTemplate,
          }));
      });
    }, // sessionBehaviorOccurrences()

    sessionProgramsData() {
      return chain(this.sessionPrograms).keyBy('id').mapValues('data').value();
    }, // sessionProgramsData()

    hasOtherLocalBackup() {
      const ignoreProperties = ['updateTimestamp', 'createTimestamp', 'updateBy',
        'currentInterval', 'duration', 'timerOffsetTimestamp', 'durationPausedTimestamp',
        'durationIsPaused'];
      const { backupData } = this;

      if (backupData) {
        const mappedBackupData = chain(backupData)
          .pickBy({ session: this.session.id })
          .mapValues(
            (sessionProgram) => (omit(sessionProgram, ignoreProperties)),
          )
          .value();

        if (!isEmpty(mappedBackupData)) {
          const serverData = mapValues(
            this.sessionProgramsData,
            (sessionProgram) => (omit(sessionProgram, ignoreProperties)),
          );

          if (!isEqual(mappedBackupData, serverData)) {
            const backupDataSize = chain(mappedBackupData)
              .map('data')
              .reduce((pV, dataItem) => pV + size(dataItem), 0)
              .value();
            const serverDataSize = chain(serverData)
              .map('data')
              .reduce((pV, dataItem) => pV + size(dataItem), 0)
              .value();

            return backupDataSize > serverDataSize;
          }
        }
      }
      return false;
    }, // hasOtherLocalBackup()

    isAsyncSession() {
      return get(this.session, 'sessionType') === 'remote_async';
    }, // isAsyncSession()

    hasTimecodedData() {
      return get(this.editSession, 'hasTimecodedData', false)
        && (
          (this.editData)
          || (chain(this.sessionProgramsWithData)
            .reject((sessionProgram) => (chain(sessionProgram).get(['data', 'data']).isEmpty().value()))
            .size()
            .value() > 0));
    }, // hasTimestampedData()

    sessionDuration() {
      if (this.editSession.duration) {
        return this.editSession.duration;
      }

      return chain(this.sessionFiles).sumBy('duration')
        .defaultTo(
          get(this.editSession, ['finishTimestamp', 'seconds'], 0)
          - get(this.editSession, ['runTimestamp', 'seconds'], 0),
        )
        .value();
    }, // sessionDuration()

    messageTimestamp() {
      if (this.currentTimestamp === this.sessionDuration) {
        return null;
      }
      return this.currentTimestamp;
    },

    currentTimestampFormatted() {
      return this.formatDuration(this.currentTimestamp);
    },

    sessionNameFormatted() {
      return this.editSession.sessionName || this.$t('sessions.sessionCard.adHocSessionTitle');
    },

    stringedSession() {
      return JSON.stringify(this.session);
    },

    programDataNotesWithNames() {
      return this.dataNotes
        .slice(0)
        .sort((first, second) => first.createTimestamp - second.createTimestamp)
        .reduce((acc, note) => {
          if (note.program && note.program in acc) {
            acc[note.program].push(note);
          } else if (note.program) {
            acc[note.program] = [note];
          }
          return acc;
        }, {});
    },

    appointmentBannerType() {
      if (!this.areAllActivitiesRendered && !this.isSomeNotesReadyToBeSend) {
        return 'warning';
      }
      if (!this.areAllActivitiesRendered && this.isSomeNotesReadyToBeSend) {
        return 'info';
      }
      if (this.areAllActivitiesRendered) {
        return 'success';
      }
      return 'info';
    }, // appointmentBannerType

    isOwnSession() {
      return this.session.runBy === this.userDetails.userId;
    },

  },

  watch: {
    stringedSession: {
      immediate: true,
      handler() {
        const sessionIdHasChanged = (this.session.id !== get(this.editSession, 'id'));

        if (!this.isEditingData || sessionIdHasChanged) {
          this.editSession = {
            sessionNotesSignatures: [],
            sessionNotesLocked: false,
            requiredSignatories: [],
            requiredSignatoriesUserIds: [],
            ...this.session,
            id: this.session.id,
          };
        }

        this.setHistoryVisibilitySettings(this.editSession);

        if (sessionIdHasChanged) {
          this.loadSessionDataWithRetries();
        }

        this.emitSessionChanged();
      },
    },

    'session.id': {
      immediate: true,
      handler(newSessionId, oldSessionId) {
        if (newSessionId === undefined) {
          return;
        }

        if (!newSessionId) {
          try {
            const messagesDb = new MessagesDB();
            messagesDb.unbind(this, 'sessionMessages');
          } catch (err) {
            console.error(err);
            logEvent(new Error('Can\'t unbind sessionMessages'));
          }

          if (oldSessionId) {
            const sessionFilesDb = new SessionFilesDB(oldSessionId);
            sessionFilesDb.unbind(this, 'sessionFiles');
          }
        }
      },
    },

    // Back/next moved to another activity of this session: switch in place. Every
    // item stays mounted, so nothing typed into the collapsed one is lost.
    'navController.currentId': {
      handler(sessionActivityId, prevSessionActivityId) {
        if (sessionActivityId && prevSessionActivityId
          && (sessionActivityId !== prevSessionActivityId)) {
          this.openSessionActivityById(sessionActivityId);
        }
      },
    },

    sessionDuration: {
      deep: false,
      immediate: true,
      handler(duration) {
        if (this.maxTimestamp !== undefined
          && this.maxTimestamp > 0
          && duration > this.maxTimestamp) {
          this.currentTimestamp = this.maxTimestamp;
        } else {
          this.currentTimestamp = duration;
        }
      },
    },

    currentTime: {
      deep: false,
      immediate: true,
      handler(currentTime) {
        if (currentTime !== null) {
          this.currentTimestamp = currentTime;
        }
      },
    },

    sessionActivities: {
      /*
       todo [vue3-migration]: should we recalculate linked appointments on every session activities array mutating
        or only on the array reassignment? Make the first option for now.
      */
      deep: true,
      immediate: true,
      async handler(sessionActivities) {
        await this.recalculateSessionActivities(sessionActivities);
      },
    },
  },

  async created() {
    // Must not be reactive
    this.sessionActivityItemRefs = {};

    await Promise.all(
      [
        this.loadPromptLevelTemplates(),
        this.bindSessionActivities(),
        this.bindBackupsData(this.session.id),
        this.bindBackupNotes(this.session.id),
      ],
    );
  },

  methods: {
    ...mapActions(sessions, ['deleteSession']),
    ...mapActions(programsTargets, ['getProgramTargets', 'getAllProgramTargets']),
    ...mapActions(promptLevels, ['loadPromptLevelTemplates']),
    ...mapActions(workflows, ['reloadWorkflows']),

    /**
     * Should be called before emitting 'close' to let browser clear session programs
     * and TrialOverviews data from the RAM memory
     *
     * @see https://github.com/vuejs/vue/issues/11955#issuecomment-798440705
     */
    async clearProgramsAndTrialsData() {
      forEach(this.$refs.trialOverviews, (trial) => {
        /* eslint-disable no-param-reassign */
        trial.data = null;
        trial.columns = null;
        trial.currentSessionProgram = null;
        /* eslint-enable no-param-reassign */
      });

      // should hide all banners so that they won't pop up before dialog close
      this.hideAllBanners = true;
      this.sessionPrograms = [];

      await this.$nextTick();
    }, // clearProgramsAndTrialsData()

    async onCloseDialog() {
      if (this.isAnySessionActivityChanged || this.isDataChanged) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('sessions.sessionCard.confirmDiscardChanges'),
          async () => {
            await this.onSuccessCloseDialog();
          },
        );
      } else {
        await this.onSuccessCloseDialog();
      }
    },

    async onSuccessCloseDialog() {
      await this.clearProgramsAndTrialsData();
      this.onCloseVisibilityMenu();
      this.$emit('close');
    },

    onNavPrev() {
      this.guardedNavigate(-1);
    }, // onNavPrev()

    onNavNext() {
      this.guardedNavigate(1);
    }, // onNavNext()

    // A step inside this session only collapses one activity and expands another,
    // so nothing is discarded. Leaving unmounts the card and loses unsaved edits,
    // which is confirmed exactly like closing the dialog.
    guardedNavigate(delta) {
      if (this.isNavBusy) {
        return;
      }

      const navigate = () => (delta < 0 ? this.navController.goPrev() : this.navController.goNext());
      // A target that is not loaded yet (null) is treated as leaving the card.
      const target = this.navController.peek(delta);
      const staysInThisSession = !!target && (target.session === this.session.id);

      if (!staysInThisSession && (this.isAnySessionActivityChanged || this.isDataChanged)) {
        this.confirmDialog(
          this.$t('common.dialogTitleConfirm'),
          this.$t('sessions.sessionCard.confirmDiscardChanges'),
          navigate,
        );
        return;
      }

      navigate();
    }, // guardedNavigate()

    async onSessionUpdated(sessionChanges) {
      assign(this.editSession, sessionChanges);
      this.$emit('changed', true);
    }, // onSessionUpdated()

    calculateTotalDurationFromTimeFrames(timeFrames) {
      if (timeFrames.length === 0) {
        return 0;
      }

      let totalDuration = 0;
      let [currentStart, currentEnd] = timeFrames[0];

      for (let i = 1; i < timeFrames.length; i += 1) {
        const [nextStart, nextEnd] = timeFrames[i];

        if (currentEnd < nextStart) {
          // No overlap
          totalDuration += currentEnd - currentStart;
          currentStart = nextStart;
          currentEnd = nextEnd;
        } else {
          // Overlap, extend the current end if needed
          currentEnd = Math.max(currentEnd, nextEnd);
        }
      }
      // Add the last time frame
      totalDuration += currentEnd - currentStart;

      return totalDuration / 1000; // return duration in seconds
    }, // calculateTotalDurationFromTimeFrames()

    calculateSessionDuration() {
      const sessionActivityTimestamps = this.sessionActivities
        .filter(({ type }) => type === 'session')
        .map((sessionActivity) => ({
          startTimestamp: sessionActivity.startTimestamp,
          endTimestamp: sessionActivity.endTimestamp,
        }))
        .filter(({ startTimestamp, endTimestamp }) => (
          startTimestamp instanceof Timestamp && endTimestamp instanceof Timestamp))
        .map(({ startTimestamp, endTimestamp }) => ([startTimestamp.toMillis(), endTimestamp.toMillis()]))
        .sort(([a], [b]) => a - b);

      return this.calculateTotalDurationFromTimeFrames(sessionActivityTimestamps);
    }, // calculateSessionDuration()

    async recalculateSessionDurationFromSessionActivities() {
      if (!this.isDone
        || this.sessionActivities.length === 0
        || this.editSession.isDurationOverridden
      ) {
        return;
      }

      const duration = this.calculateSessionDuration();
      if (duration !== this.session.duration) {
        const sessionsDb = new SessionsDB();
        const sessionChanges = { duration };
        assign(this.editSession, sessionChanges);
        await sessionsDb.set(sessionChanges, this.session.id);
        if (duration > 0) {
          await this.resyncProgramsDuration();
        }
      }
    }, // recalculateSessionDurationFromSessionActivities()

    async onSessionActivityTimestampsChanged(sessionActivity) {
      if (sessionActivity?.id) {
        try {
          await this.navController?.refreshRow?.(sessionActivity.id);
        } catch (err) {
          console.error('Failed to refresh navigation activity:', err);
          logEvent(err);
        }
      }

      return this.recalculateSessionDurationFromSessionActivities();
    }, // onSessionActivityTimestampsChanged()

    async onSessionActivityDeleteRequest(sessionActivity) {
      await this.deleteSessionActivity(sessionActivity);
      this.notifySuccess(this.$t('sessions.sessionCard.notifyDeleteSessionActivity'));
      await this.recalculateSessionDurationFromSessionActivities();
      this.$emit('delete-session-activity', sessionActivity.id);
    }, // onSessionActivityDeleteRequest()

    async onSaveSessionName() {
      this.editSessionName = !this.editSession.sessionName;
      const sessionChanges = {
        sessionName: this.editSession.sessionName,
      };
      const sessionActivityChanges = {
        sessionName: this.editSession.sessionName,
      };
      const sessionsDb = new SessionsDB();
      const sessionActivitiesDb = new SessionActivitiesDB();
      const sessionActivitiesActions = map(
        this.sessionActivities,
        (sessionActivity) => (sessionActivitiesDb.set(sessionActivityChanges, sessionActivity.id)),
      );
      return Promise.all([
        sessionsDb.set(sessionChanges, this.editSession.id),
        ...sessionActivitiesActions,
      ]);
    },

    onSwitchSessionActivity(sessionActivity, isOpen) {
      this.sessionActivityExpand[sessionActivity.id] = isOpen;

      if (isOpen) {
        this.navController?.setCurrent?.(sessionActivity);

        if (
          (typeof sessionActivity.sessionDurationOffset === 'number')
          && (typeof sessionActivity.sessionDurationEnd === 'number')
        ) {
          this.setMinMaxTime(
            sessionActivity.id,
            sessionActivity.sessionDurationOffset,
            sessionActivity.sessionDurationEnd,
          );
        }

        return;
      }

      if (this.focusedSessionActivity === sessionActivity.id) {
        this.focusedSessionActivity = null;
        this.minTimestamp = undefined;
        this.maxTimestamp = undefined;
        this.currentTimestamp = this.sessionDuration;
      }
    },

    onAddSessionActivity() {
      const sessionActivity = {
        startTimestamp: this.session.runTimestamp || null,
        endTimestamp: this.session.finishTimestamp || null,
        startGeolocation: null,
        endGeolocation: null,
        status: 'finished',
        type: 'session',
        user: this.userDetails.userId,
        userName: this.userDetails.name,
        clinic: this.session.clinic,
        client: this.session.client,
        notes: '',
        isDeleted: false,
      };
      this.openComponentDialog(EditSessionActivityDialog, {
        sessionActivity,
      }, async (sessionActivityChanges) => {
        const sessionActivitiesDb = new SessionActivitiesDB();
        const createdSessionActivity = await sessionActivitiesDb.create({
          ...sessionActivity,
          ...sessionActivityChanges,
          session: this.session.id,
          sessionName: this.session.sessionName || null,
          appointment: null,
          createdBy: this.userDetails.userId,
        });

        this.$emit('add-session-activity', createdSessionActivity);
        await this.recalculateSessionDurationFromSessionActivities();
      });
    },

    onSetSessionActivityChanged(sessionActivity, isChanged) {
      this.sessionActivityChanged[sessionActivity.id] = isChanged;
      this.emitSessionChanged();
    },

    async bindSessionActivities() {
      if (!this.session) {
        return;
      }
      this.isLoadingSessionActivities = true;
      const sessionActivitiesDb = new SessionActivitiesDB();

      // Unfortunately Firestore rules currently doesn't let us query for null values
      // in and "in" query, so we have to query for both null and non-null values separately
      await sessionActivitiesDb.bindAll(
        this,
        'sessionActivities',
        [
          ['clinic', '==', this.session.clinic],
          ['client', '==', this.session.client],
          ['session', '==', this.session.id],
          ['isDeleted', '==', false],
        ],
      );
      const openedSessionActivityId = this.navController?.currentId || this.openSessionActivity;

      let defaultOpenedSessionActivity;
      if (openedSessionActivityId) {
        defaultOpenedSessionActivity = find(this.sessionActivities, { id: openedSessionActivityId });
      } else {
        defaultOpenedSessionActivity = chain(this.sessionActivities)
          .filter((sessionActivity) => (
            (
              (sessionActivity.status === 'finished')
            && (sessionActivity.user === this.userDetails.userId)
            ) || (
              (sessionActivity.status === 'pending-signatures')
            && (find(sessionActivity.requiredSignatories, { userId: this.userDetails.userId }))
            )
          ))
          .head()
          .value();
      }

      if (defaultOpenedSessionActivity) {
        this.expandSessionActivity(defaultOpenedSessionActivity);
      }
      this.isLoadingSessionActivities = false;
    },

    // Collapses the others; they stay mounted, so their unsaved input survives
    expandSessionActivity(sessionActivity) {
      this.sessionActivityExpand = {
        [sessionActivity.id]: true,
      };
      this.setMinMaxTime(
        sessionActivity.id,
        sessionActivity.sessionDurationOffset,
        sessionActivity.sessionDurationEnd,
      );
    }, // expandSessionActivity()

    openSessionActivityById(sessionActivityId) {
      const sessionActivity = find(this.sessionActivities, { id: sessionActivityId });
      if (!sessionActivity) {
        return;
      }
      this.navController?.setCurrent?.(sessionActivity);
      this.expandSessionActivity(sessionActivity);
      this.$nextTick(() => this.scrollToSessionActivity(sessionActivityId));
    }, // openSessionActivityById()

    setSessionActivityItemRef(sessionActivityId, el) {
      if (el) {
        this.sessionActivityItemRefs[sessionActivityId] = el;
      } else {
        delete this.sessionActivityItemRefs[sessionActivityId];
      }
    }, // setSessionActivityItemRef()

    scrollToSessionActivity(sessionActivityId) {
      const element = this.sessionActivityItemRefs[sessionActivityId]?.$el;
      if (!element) {
        return;
      }
      const { getScrollTarget, setVerticalScrollPosition } = scroll;
      setVerticalScrollPosition(getScrollTarget(element), element.offsetTop, 300);
    }, // scrollToSessionActivity()

    unbindSessionActivities() {
      const sessionActivitiesDb = new SessionActivitiesDB();
      return sessionActivitiesDb.unbind(this, 'sessionActivities');
    },

    scrollToSessionData() {
      const { getScrollTarget, setVerticalScrollPosition } = scroll;
      const target = getScrollTarget(this.$refs.sessionDataAnchor);
      const offset = this.$refs.sessionDataAnchor.offsetTop;
      setVerticalScrollPosition(target, offset, 500);
    },

    emitSessionChanged() {
      this.$emit('changed', this.isAnySessionActivityChanged || this.isDataChanged);
    },

    async onMessageRead(message) {
      const messageTimestamp = get(message, 'createTimestamp');

      const userRead = get(this.session, ['messagesLastReadBy', this.userDetails.userId]);

      if (!(messageTimestamp instanceof Timestamp)) {
        return;
      }
      if (!(userRead instanceof Timestamp)
        || (userRead < messageTimestamp)) {
        const sessionsDb = new SessionsDB();
        await sessionsDb.set(
          {
            messagesLastReadBy: {
              [this.userDetails.userId]: messageTimestamp,
            },
          },
          this.session.id,
        );
      }
    }, // onMessageRead()

    setMinMaxTime(selectedId, sessionDurationOffset = null, sessionDurationEnd = null) {
      this.focusedSessionActivity = selectedId;

      if ((sessionDurationOffset === null) || (sessionDurationEnd === null)) {
        this.minTimestamp = 0;
        this.maxTimestamp = (this.session.finishTimestamp - this.session.runTimestamp);
      } else {
        this.minTimestamp = sessionDurationOffset;
        this.maxTimestamp = sessionDurationEnd;
      }

      if (this.maxTimestamp > this.sessionDuration) {
        this.maxTimestamp = this.sessionDuration;
      }

      const currentTimestamp = this.maxTimestamp;
      if ((currentTimestamp >= 0) && (currentTimestamp <= this.sessionDuration)) {
        this.currentTimestamp = currentTimestamp;
      } else {
        this.currentTimestamp = this.sessionDuration;
      }
    },

    injectBackupToActivities(activityId) {
      const backupNotes = this.getBackupNotes(activityId);
      this.sessionActivitiesWithBackupNotes = map(
        this.sessionActivities,
        (sessionActivity) => {
          const injection = backupNotes.fieldsData
            ? { sessionNotesFieldData: backupNotes.fieldsData }
            : { notes: backupNotes.notes };

          return sessionActivity.id !== activityId
            ? sessionActivity
            : {
              ...sessionActivity,
              ...injection,
              id: sessionActivity.id,
            };
        },
      );
    }, // injectBackupToActivities()

    activityHasNotesBackup(activityId) {
      const unchangedActivity = find(this.sessionActivities, { id: activityId });
      const notesFromBackup = this.getBackupNotes(activityId);

      if (isEmpty(notesFromBackup) || notesFromBackup.isBackupAddedOnline) {
        return false;
      }

      const { notes, fieldsData } = notesFromBackup;

      if (notes) {
        return isString(notes) && (notes !== unchangedActivity.notes);
      }

      const fieldsToIgnore = ['sessionDuration', 'sessionFinishTime', 'sessionStartTime'];
      const dataToIgnore = chain(fieldsToIgnore)
        .map((field) => {
          if (has(fieldsData, `session.${field}`)) {
            return [field, fieldsData.session[field]];
          }

          return null;
        })
        .compact()
        .fromPairs()
        .value();
      const notesFromActivity = this.handledActivitySessionTimeFields(unchangedActivity, dataToIgnore);

      return !isEqual(get(notesFromActivity, 'sessionNotesFieldData'), fieldsData);
    }, // activityHasNotesBackup()

    handledActivitySessionTimeFields(activity, dataToIgnore) {
      const sessionFields = {
        ...get(activity, ['sessionNotesFieldData', 'session']),
        ...dataToIgnore,
      };
      return {
        ...activity,
        sessionNotesFieldData: {
          ...get(activity, 'sessionNotesFieldData'),
          ...(has(activity, ['sessionNotesFieldData', 'session']) && { session: sessionFields }),
        },
      };
    }, // handledActivitySessionTimeFields()

    onShowBackupNotes(sessionActivity, evt) {
      this.injectBackupToActivities(sessionActivity.id);
      this.showNotesBackup = evt;
    }, // onShowBackupNotes()

    onViewLocalBackup() {
      this.showBackupData = true;
    }, // onViewLocalBackup()

    onViewOriginalData() {
      this.showBackupData = false;
    }, // onViewOriginalData()

    onRestoreBackupData() {
      if (!this.hasOtherLocalBackup) {
        return;
      }

      const { backupData } = this;

      if (isEmpty(backupData)) {
        return;
      }

      this.confirmDialog(
        this.$t('run.collectData.restoreBackup.title'),
        this.$t('sessions.sessionCard.confirmRecoverMessage'),
        (async () => {
          // Restore backup data
          const dataDb = new DataDB();
          const dataActions = map(backupData, (sessionProgramData) => {
            const dataId = get(sessionProgramData, 'id', null);
            if (dataId) {
              return dataDb.set(omit(sessionProgramData, ['createTimestamp', 'updateTimestamp']), dataId);
            }
          });
          await Promise.all(dataActions);
          await clearClientBackupData(this.session.client);
          this.showBackupData = false;
          this.loadSessionData();
        }),
      );
    }, // onRestoreBackupData()

    onSeek(timestamp) {
      this.currentTimestamp = timestamp;
      this.onGotoTimestamp(timestamp);
    }, // onSeek()

    onClickChatMessage(chatMessage) {
      if (chatMessage.sessionTimeOffset) {
        this.onGotoTimestamp(chatMessage.sessionTimeOffset);
      }
    },

    onGotoTimestamp(timestamp) {
      if (timestamp === null) {
        return;
      }

      this.currentTimestamp = timestamp;
      this.$emit('video-seek', timestamp);
    }, // onGotoTimestamp()

    async onEditSessionProgramsData() {
      this.editData = true;
      this.preloadWorkflows();
      this.showMenu = false;
      this.isDataChanged = true;
      this.scrollToSessionData();
      this.emitSessionChanged();
    }, // onEditSessionProgramsData()

    async onSaveSessionProgramsData() {
      await this.saveDataNotes();
      await this.saveSessionPrograms();
      this.isDataChanged = false;
      this.emitSessionChanged();
    }, // onSaveSessionProgramsData()

    /**
     * Event: Loads sessionPrograms and associated data for the session
     */
    async onCancelEditSessionPrograms() {
      this.endSessionDataEdit();
    }, // onCancelEditSessionPrograms()

    async onEditSessionTime() {
      this.openComponentDialog(EditSessionDetailsDialog, {
        session: this.editSession,
      }, async (sessionChanges) => {
        const sessionsDb = new SessionsDB();
        assign(this.editSession, sessionChanges);
        await sessionsDb.set(sessionChanges, this.editSession.id);

        await this.resyncProgramsDuration();

        this.$emit('changed', true);

        this.notifySuccess(this.$t('sessions.sessionCard.notifyEditSessionDetailsSuccess'));
      });
    }, // onEditSessionTime()

    /**
     * Delete a session
     */
    async onDeleteSession() {
      if (this.hasLinkedAppointments || this.cantDeleteActivities) {
        return;
      }

      this.$q
        .dialog({
          title: this.$t('common.dialogTitleConfirm'),
          message: this.$t('sessions.sessionCard.notifyDeleteSessionConfirm'),
          cancel: true,
          persistent: true,
        })
        .onOk(async () => {
          this.$q.loading.show();
          try {
            await this.deleteSession(this.editSession.id);
            this.notifySuccess(this.$t('sessions.sessionCard.notifyDeleteSessionSuccess'));
            this.$emit('delete-session', this.editSession.id);
          } catch (err) {
            this.notifyNegative(err.message);
          } finally {
            this.$q.loading.hide();
          }
        });
    }, // onDeleteSession()

    onCopySupportInfo() {
      copySupportInfoToClipboard({ session: this.editSession?.id });
    }, // onCopySupportInfo()

    /**
     * Event dispatched by asset when a video started playing
     */
    onPlayVideo(videoInfo) {
      this.$emit('play-video', videoInfo);
    }, // onPlayVideo()

    /**
     * Event dispatched by asset when video information has been updated
     */
    onUpdateVideoInfo(videoInfo) {
      this.$emit('update-video-info', videoInfo);
    }, // onUpdateVideoInfo()

    async onFileDelete(assetType) {
      let missingAssets = false;

      const isVideo = assetType === 'video';

      if (isVideo) {
        missingAssets = chain(this.sessionFiles)
          .filter({ type: 'application/x-mpegURL' })
          .isEmpty()
          .value();
      } else {
        missingAssets = chain(this.sessionFiles)
          .reject({ type: 'application/x-mpegURL' })
          .isEmpty()
          .value();
      }

      if (missingAssets) {
        const updateField = isVideo ? 'hasVideo' : 'hasAttachments';

        const sessionsDb = new SessionsDB();

        const updateData = {
          [updateField]: false,
        };

        assign(this.editSession, updateData);
        await sessionsDb.set(updateData, this.editSession.id);
      }
    }, // onFileDelete()

    async onCopyDataToClipboard() {
      //  Initialize header
      //  For each session program
      let exportData = 'PROGRAM\tTARGET\tPROMPT CODES\tRESULT\n';
      exportData += chain(this.$refs.trialOverviews)
        .map((trialOverview) => trialOverview.exportData())
        .compact()
        .join('\n')
        .value();

      if (this.$q.platform.is.capacitor) {
        await Clipboard.write({
          string: exportData,
        });
      } else {
        await copyToClipboard(exportData);
      }

      this.notifySuccess(this.$t('sessions.sessionCard.notifyCopyToClipboardComplete'));
    }, // onCopyDataToClipboard()

    async onTransferSessionNote(sessionActivityId = null, overrideAppointmentData = null) {
      this.isSendingSessionNote = true;
      try {
        if (overrideAppointmentData) {
          this.sessionActivities = this.sessionActivities.map((sessionActivity) => {
            if (sessionActivity.id === sessionActivityId) {
              return {
                ...sessionActivity,
                ...overrideAppointmentData,
              };
            }

            return sessionActivity;
          });
        }

        const transferActions = this.sessionActivities.reduce((acc, sessionActivity) => {
          if (sessionActivityId && sessionActivity.id !== sessionActivityId) {
            return acc;
          }

          const shouldInclude = (
            (
              sessionActivity.appointment
                && (
                  sessionActivity.status === 'closed'
                  || (sessionActivity.status === 'pending-signatures' && this.allowImmediateSync
                    // For beta Camber + Scheduling we don't allow immediate sync
                    && !this.integrationSettings.allowScheduling)
                )
            )
              || this.canTransferStaffActivityWithoutLinkedAppointment(sessionActivity)
          );

          if (shouldInclude) {
            acc.push(this.transferSessionActivityNote(sessionActivity));
          }

          return acc;
        }, []);

        const transferResults = await Promise.allSettled(transferActions);
        await this.linkedAppointmentsUpdated(sessionActivityId);

        const failedTransfers = transferResults.filter(({ status }) => status === 'rejected');

        if (failedTransfers.length) {
          await Promise.all(
            failedTransfers.map(({ reason }) => this.handleTransferError(reason)),
          );
          return;
        }

        this.notifySuccess(this.$t('sessions.sessionCard.notifyTransferSuccess', {
          integration: this.getIntegrationName(this.connectedClinic.integration),
        }));
      } catch (err) {
        notifyErrorWithDetails(this.$t('sessions.sessionCard.notifyTransferError', {
          integration: this.getIntegrationName(this.connectedClinic.integration),
          error: err.message,
        }), err.details?.fullMessage);
        logEvent(err);
      } finally {
        this.isSendingSessionNote = false;
      }
    }, // onTransferSessionNote()

    async transferSessionActivityNote(sessionActivity) {
      const { base64pdf, senderInfo } = await this.getSessionActivityNotesDataToTransfer(sessionActivity);

      return this.transferSessionToIntegration(
        sessionActivity.appointment,
        sessionActivity,
        base64pdf,
        this.sessionUrl,
        senderInfo,
      );
    }, // transferSessionActivityNote()

    setHistoryVisibilitySettings(session) {
      // For backwards compatibility we promote history visibility to session visibility
      this.visibilitySettings = {
        limitedVisibility: get(session, 'limitedVisibility', false),
        visibility: get(session, 'visibility', []),
        limitHistoryVisibility: true,
      };
    }, // setHistoryVisibilitySettings()

    isSessionProgramVisible(sessionProgram) {
      if (!this.hasProgram(sessionProgram)) {
        return false;
      }
      if (this.editData
        || get(sessionProgram, ['data', 'notes'])
        || this.hasSessionProgramFiles(sessionProgram)
        || this.hasSupervisionNotes(sessionProgram)
      ) {
        return true;
      }
      return !chain(sessionProgram).get(['data', 'data']).isEmpty().value();
    }, // isSessionProgramVisible()

    hasProgram(sessionProgram) {
      return !!find(this.allPrograms, { id: sessionProgram.program });
    }, // hasProgram()

    getProgram(sessionProgram) {
      return find(this.allPrograms, { id: sessionProgram.program });
    }, // getProgram()

    hasTaskList(sessionProgram) {
      return !!get(sessionProgram, ['program', 'associatedTaskList'])
        && (!isEmpty(this.getTaskList(sessionProgram)));
    }, // hasTaskList()

    getTaskList(sessionProgram) {
      return chain(this.sessionTaskLists)
        .filter(
          {
            taskList: get(
              sessionProgram,
              ['program', 'associatedTaskList'],
            ),
          },
        )
        .sortBy('createTimestamp')
        .last()
        .value();
    }, // getTaskList()

    getTaskListItems(sessionProgram) {
      return get(sessionProgram, ['program', 'associatedTaskListItems'], null);
    }, // getTaskListItems()

    hasSessionProgramFiles(sessionProgram) {
      return !chain(this.sessionProgramFiles)
        .filter({ sessionProgram: get(sessionProgram, 'id') })
        .isEmpty()
        .value();
    }, // hasSessionProgramFiles()

    getSessionProgramFiles(sessionProgram) {
      return chain(this.sessionProgramFiles)
        .filter({ sessionProgram: get(sessionProgram, 'id') })
        .value();
    }, // getSessionProgramFiles()

    async resyncProgramsDuration() {
      const sessionProgramDataChanges = this.sessionPrograms
        .filter((sessionProgram) => {
          const originalProgram = this.allPrograms.find(({ id }) => id === sessionProgram.program);
          // Programs with timerAutoStart or pinned frequency programs should be resynced to reflect the new
          // session duration, other programs should keep their original duration
          return originalProgram?.timerAutoStart || (sessionProgram.pinned && originalProgram.type === 'frequency');
        })
        .map((sessionProgram) => ({
          id: sessionProgram.data.id,
          changes: {
            duration: this.editSession.duration,
          },
        }));

      // Sync duration back to in-memory sessionPrograms
      // Intentionally do this BEFORE saving to DB to avoid lag in the UX
      // and ensure that changes are maintained on consecutive edits without having to wait for DB roundtrip
      sessionProgramDataChanges.forEach(({ id }) => {
        const sessionProgram = this.sessionPrograms.find((sp) => sp.data.id === id);
        if (sessionProgram) sessionProgram.data.duration = this.editSession.duration;
      });

      // Save duration for data associated with programs that should be resynced to session duration
      const dataDb = new DataDB();
      const dataActions = sessionProgramDataChanges
        .map((sessionProgramData) => dataDb.create(
          sessionProgramData.changes,
          sessionProgramData.id,
          true,
          this.editSession.runTimestamp,
        ));
      await Promise.all(dataActions);
    }, // resyncProgramsDuration()

    async loadDataNotes() {
      const dataNotesDb = new DataNotesDB();
      this.dataNotes = await dataNotesDb.readAll(
        [['client', '==', this.editSession.client],
          ['session', '==', this.editSession.id]],
      );
    }, // loadDataNotes()

    async loadSessionComments() {
      const messagesDb = new MessagesDB();
      try {
        this.isLoadingComments = true;
        await messagesDb.bindAll(
          this,
          'sessionMessages',
          [['clinic', '==', this.userDetails.clinic],
            ['client', '==', this.editSession.client],
            ['thread', '==', `session_${this.editSession.id}`]],
          'createTimestamp',
          'asc',
        );
      } finally {
        this.isLoadingComments = false;
      }
    }, // loadSessionComments()

    async loadSessionFiles() {
      const sessionFilesDb = new SessionFilesDB(this.editSession.id);
      await sessionFilesDb.bindAll(this, 'sessionFiles');

      if (this.session.hasVideo) {
        try {
          this.fixVideosLoading = true;

          await callFirebaseFunction('fixSessionVideoUrls', { sessionId: this.session.id });
        } catch (err) {
          console.error(err);

          if (process.env.ENV_TYPE !== 'development') {
            logEvent(err);
          }
        } finally {
          this.fixVideosLoading = false;
        }
      }

      // this.sessionFiles = await sessionFilesDb.readAll();
    }, // loadSessionFiles()

    async loadTaskLists() {
      const taskListsDb = new TaskListsDB(this.editSession.client);
      await taskListsDb.bindAll(
        this,
        'sessionTaskLists',
        [
          ['status', '==', 'active'],
        ],
      );
    }, // loadTaskLists()

    async loadSessionDataWithRetries() {
      let loadTries = this.isSessionTypeWithoutPrograms ? 1 : 5;

      while (isEmpty(this.sessionPrograms) && loadTries > 0) {
        // eslint-disable-next-line no-await-in-loop
        await this.loadSessionData();

        if (isEmpty(this.sessionPrograms)) {
          loadTries -= 1;

          if (loadTries === 0) {
            logEvent(new Error('Unable to load session data'), {
              context: {
                context_data: {
                  sessionId: this.editSession.id,
                },
              },
            });
          }
        } else {
          loadTries = 0;
        }
      }
    }, // loadSessionDataWithRetries()

    async loadSessionPrograms() {
      const sessionProgramsDb = new SessionProgramsDB(this.editSession.id);
      this.sessionPrograms = await sessionProgramsDb
        .readWithData('order', 'asc', this.session.client);
    }, // loadSessionPrograms()

    async preloadWorkflows() {
      this.isPreloadComplete = false;
      const workflowIds = new Set();
      let programTargets = this.sessionPrograms.map((sessionProgram) => {
        if (!this.isSessionProgramVisible(sessionProgram)) {
          return [];
        }
        const program = this.getProgram(sessionProgram);
        workflowIds.add(program.workflow);
        return this.getAllProgramTargets({
          program: program.id,
        });
      });
      programTargets = (await Promise.all(programTargets)).flat();
      programTargets.forEach((target) => {
        workflowIds.add(target.workflow);
        workflowIds.add(target.defaultWorkflow);
      });
      await this.reloadWorkflows([...workflowIds]);
      this.isPreloadComplete = true;
    },

    async loadSessionData(isEndingDataEditing = false) {
      this.$q.loading.show({
        message: this.$t('sessions.sessionCard.loadingSessionDataMessage'),
      });

      try {
        await this.loadSessionPrograms();
        if (!isEndingDataEditing) {
          this.ensureEditDataCondition();
        }
        this.preloadWorkflows();
        this.loadDataNotes();
        this.loadSessionComments();
        this.loadSessionFiles();
        this.loadTaskLists();
      } finally {
        this.$q.loading.hide();
      }
    }, // loadSessionData()

    async endSessionDataEdit() {
      this.$q.loading.show({
        message: this.$t('sessions.sessionCard.loadingSessionDataMessage').toString(),
      });
      try {
        this.editData = false;
        this.isDataChanged = false;
        this.emitSessionChanged();
        await this.loadSessionData(true);
      } finally {
        this.$q.loading.hide();
      }
    }, // endSessionDataEdit()

    async saveDataNotes() {
      const dataNotesDb = new DataNotesDB();
      const saveActions = chain(this.dataNotes)
        .filter({ editStatus: 'update' })
        .map((dataNotesItem) => {
          return dataNotesDb.set(
            omit(dataNotesItem, ['id', 'editStatus']),
            dataNotesItem.id,
          );
        })
        .value();
      return Promise.all(saveActions);
    },

    async saveSessionPrograms() {
      this.$q.loading.show();
      try {
        const sessionsDb = new SessionsDB();

        const saveObj = {
          runTimestamp: this.editSession.runTimestamp || null,
          finishTimestamp: this.editSession.finishTimestamp || null,
          runBy: this.editSession.runBy,
          runByName: this.editSession.runByName,
        };

        if (this.editSession.duration) {
          saveObj.duration = this.editSession.duration;
        }

        // Save session info
        await sessionsDb.create(saveObj, this.editSession.id, true);

        // Save data
        await this.saveSessionProgramsData();
        this.programsDataChanged = false;

        this.$emit('update-data', saveObj);

        this.notifySuccess(this.$t('sessions.sessionCard.notifySessionSavedSuccess'));
      } catch (err) {
        this.notifyNegative(err.message);
      } finally {
        this.endSessionDataEdit();
        this.$q.loading.hide();
      }
    }, // saveSessionPrograms()

    async saveSessionProgramsData() {
      const sessionProgramsData = await Promise.all(
        chain(this.sessionPrograms)
          .map((sessionProgram) => get(sessionProgram, 'data'))
          .compact()
          .map(async (sessionProgramData) => {
            const programTargets = await this.getProgramTargets({
              program: sessionProgramData.program,
            });

            const targetsLabels = this.getTargetsLabelsKeyedById(programTargets);

            return {
              ...sessionProgramData,
              clinic: this.userDetails.clinic,
              createTimestamp: this.editSession.runTimestamp,
              targetsLabels,
            };
          })
          .value(),
      );

      const dataDb = new DataDB();
      const createActions = sessionProgramsData
        .map((sessionProgramData) => dataDb.create(
          sessionProgramData,
          get(sessionProgramData, 'id', null),
          false,
          this.editSession.runTimestamp,
        ));
      return Promise.all(createActions);
    }, // saveSessionProgramsData()

    formatTime(fbTimestamp) {
      return fbTimestamp ? this.getLocaleTimeString(fbTimestamp, { options: { timeStyle: 'short' } }) : '';
    }, // formatTime()

    getLinkedAppointment(sessionActivity) {
      return chain(this.linkedAppointments)
        .find({ id: sessionActivity.appointment })
        .defaultTo({})
        .value();
    },

    onEditSessionName() {
      if (!this.userCanEditData || this.strictSessionNaming) {
        return;
      }
      this.editSessionName = true;

      this.$nextTick(() => {
        if (this.$refs.sessionName) {
          this.$refs.sessionName.focus();
        }
      });
    }, // onEditSessionName()

    onProgramsDataChanged(sessionProgram = null) {
      this.programsDataChanged = true;
      if (this.hasTaskList(sessionProgram)) {
        this.checkTaskListDataChange(sessionProgram);
      }
    }, // onProgramsDataChanged()

    onProgramsDataNotesChanged(sessionProgram, dataNotesChanges) {
      const idx = this.dataNotes.findIndex((note) => {
        return note.sessionProgram === sessionProgram.id
          && (dataNotesChanges.sessionActivity ? note.sessionActivity === dataNotesChanges.sessionActivity : true);
      });
      if (idx === -1) {
        return;
      }
      this.dataNotes[idx] = {
        ...this.dataNotes[idx],
        ...dataNotesChanges,
        editStatus: 'update',
      };
    },

    async onSessionProgramDataNotesChanged(sessionProgram, data) {
      const sessionProgramId = findIndex(this.sessionPrograms, { id: sessionProgram.id });
      if (sessionProgramId === -1) {
        return;
      }

      this.sessionPrograms[sessionProgramId].data.notes = data.programNote;

      // It will be triggered only in the end of the session, because there is no 'Save Data' button
      if (!this.editData) {
        await this.saveSessionProgramsData();
      }
    },

    allChangesSaved() {
      const ignoredFields = ['id', 'limitedVisibility', 'visibility',
        'limitHistoryVisibility', 'hasVideo', 'hasAttachments', 'sessionNotesLocked', 'sessionNotesSignatures',
        'requiredSignatories'];

      const original = chain(this.session).cloneDeep().omit(ignoredFields).value();
      const editedSession = chain(this.editSession).cloneDeep().omit(ignoredFields).value();

      return !this.programsDataChanged && isEqual(original, editedSession);
    }, // allChangesSaved()

    checkTaskListDataChange(sessionProgram) {
      // If all sub-targets has a positive response then suggest to mark
      // task list items completed
      if (
        chain(sessionProgram)
          .get(['program', 'targets'])
          .reject({ parentTarget: null })
          .map((target) => (
            !chain(sessionProgram)
              .get(['data', 'data'])
              .find({ target: target.id, scoreValue: 100 })
              .isEmpty()
              .value()))
          .every()
          .value()
      ) {
        const taskListCard = chain(this.$refs).get(`taskList_${sessionProgram.id}`).head().value();

        if (taskListCard) {
          forEach(this.getTaskListItems(sessionProgram), (taskListItem) => {
            taskListCard.markTaskCompleted(taskListItem);
          });
        }
      }
    }, // checkTaskListDataChange()

    hasSupervisionNotes(sessionProgram) {
      return this.getDataNotes(sessionProgram).some(({ supervisionNotes }) => supervisionNotes);
    },

    getDataNotes(sessionProgram) {
      if (!Object.keys(this.programDataNotesWithNames).length) {
        return [];
      }
      const program = sessionProgram.program?.id || sessionProgram.program;
      return this.programDataNotesWithNames[program] || [];
    },

    async recalculateSessionActivities(sessionActivities, forceUpdate, overrideAll = true) {
      if (isEmpty(sessionActivities) && overrideAll) {
        this.linkedAppointments = [];
        return;
      }

      this.isLoadingAppointments = true;
      const appointmentsDb = new AppointmentsDB();

      const appointments = chain(sessionActivities)
        .filter('appointment')
        .map((sessionActivity) => {
          const existingAppointment = find(this.linkedAppointments, { id: sessionActivity.appointment });
          // treat cache as stale if appointment shows rendered but session activity no longer is —
          // this can happen when unrender runs and the watcher fires before the forced re-fetch completes
          const isCacheStale = existingAppointment?.isRendered
            && !['rendered', 'integration-completed'].includes(sessionActivity.status);
          if (existingAppointment && !forceUpdate && !isCacheStale) {
            return existingAppointment;
          }
          return appointmentsDb.read(sessionActivity.appointment);
        })
        .value();

      try {
        const loadedAppointments = compact(await Promise.all(appointments));

        this.linkedAppointments = overrideAll
          ? loadedAppointments
          : unionBy(loadedAppointments, this.linkedAppointments, 'id');
      } catch (err) {
        this.linkedAppointments = [];
        console.error(err);
        logEvent(new Error('Can\'t load linked appointments'));
      } finally {
        this.isLoadingAppointments = false;
      }
    },

    async linkedAppointmentsUpdated(sessionActivityId = null) {
      const sessionActivities = sessionActivityId
        ? this.sessionActivities.filter(({ id, appointment }) => id === sessionActivityId && !!appointment)
        : this.sessionActivities;

      await this.recalculateSessionActivities(sessionActivities, true, false);
    },

    scrollToBannerEvent(bannerElement) {
      this.$emit('scroll-to-banner', bannerElement);
    }, // scrollToBannerEvent

    ensureEditDataCondition() {
      if (this.editData) {
        return;
      }
      const editRequested = this.isEditingData && this.userCanEditData;
      const hasData = this.sessionPrograms.some((value) => value.data?.data?.length);
      const initialManualDataEntry = !hasData && this.isOwnSession;

      if (editRequested && (this.userHasPrivilege('sessions/editData') || initialManualDataEntry)) {
        this.editData = true;
        this.isDataChanged = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.session-details-card__header--sticky {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: #fff;

  .body--dark & {
    background-color: $dark;
  }
}

.asset {
  .asset-file-attachment {
    width: 250px;
    height: 24vh;

    :deep(button) {
      height: inherit !important;
      width: inherit !important;
    }
  }
}

.unfocused-activity {
  opacity: 0.6;
}

.no-stretch {
  width:1%;
  white-space: nowrap;
}

.assets-container {
  height: 25vh;
}

.asset-margin-bottom {
  margin-bottom: 16px;
}

.comment-actions {
  padding-bottom: 20px;
}

.parents-file-attachment:deep(.q-btn__wrapper) {
  padding: 0;
}

.input-time-wrapper {
  :deep(input[type="time"]::-webkit-calendar-picker-indicator) {
    display: none;
  }

  :deep(.q-field__append) {
    padding-left: 0;
  }
}

</style>
